import { useMemo } from 'react';
import { Link } from 'wouter';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationsContext';
import type { TaskNotification, TaskNotificationStatus } from '@/lib/api';
import {
  AlertTriangle,
  Bell,
  CalendarClock,
  CheckCheck,
  CheckCircle2,
  CircleDot,
  ExternalLink,
  Inbox,
  RefreshCcw,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const SECTIONS: Array<{
  id: TaskNotificationStatus;
  label: string;
  color: string;
  bg: string;
  hint: string;
  Icon: React.ComponentType<{ className?: string }>;
}> = [
  {
    id: 'behind',
    label: 'Просрочено',
    color: '#E85D04',
    bg: 'rgba(232,93,4,0.08)',
    hint: 'Срок окончания уже прошёл, а задача ещё в работе. Требует внимания в первую очередь.',
    Icon: AlertTriangle,
  },
  {
    id: 'active',
    label: 'В работе',
    color: '#00A651',
    bg: 'rgba(0,166,81,0.08)',
    hint: 'Задачи, которые сейчас ведутся.',
    Icon: CircleDot,
  },
  {
    id: 'planned',
    label: 'В плане',
    color: '#0055A5',
    bg: 'rgba(0,85,165,0.08)',
    hint: 'Карточки из колонки «Сделать» — запланированные к работе, но ещё не взятые.',
    Icon: CalendarClock,
  },
  {
    id: 'backlog',
    label: 'Бэклог',
    color: '#7C3AED',
    bg: 'rgba(124,58,237,0.08)',
    hint: 'Карточки в колонке «Бэклог» — идеи и задачи впрок, ещё не в плане.',
    Icon: Inbox,
  },
  {
    id: 'done',
    label: 'Выполнено',
    color: '#8B9BB4',
    bg: 'rgba(139,155,180,0.12)',
    hint: 'Задачи, которые вы завершили.',
    Icon: CheckCircle2,
  },
];

function formatRange(n: TaskNotification): string {
  const fmt = (iso: string | null) => {
    if (!iso) return '';
    try {
      const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
      return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return '';
    }
  };
  const s = fmt(n.start);
  const e = fmt(n.end);
  if (s && e) return `${s} — ${e}`;
  return s || e || '';
}

const KIND_META: Record<'gantt' | 'backlog', { label: string; bg: string; color: string; title: string }> = {
  gantt: {
    label: 'Гант',
    bg: 'rgba(0, 85, 165, 0.12)',
    color: '#0055A5',
    title: 'Задача на диаграмме Ганта',
  },
  backlog: {
    label: 'Канбан',
    bg: 'rgba(139, 92, 246, 0.14)',
    color: '#7C3AED',
    title: 'Карточка на канбан-доске',
  },
};

function deepLinkFor(n: TaskNotification): string {
  const kind = n.taskKind ?? 'gantt';
  const param = kind === 'backlog' ? 'card' : 'task';
  return `/project/${n.projectId}?${param}=${encodeURIComponent(n.taskId)}`;
}

function NotificationCard({
  n,
  color,
  onRead,
}: {
  n: TaskNotification;
  color: string;
  onRead: () => void;
}) {
  const unread = !n.isRead && !n.isResolved;
  const kindMeta = KIND_META[n.taskKind ?? 'gantt'];
  return (
    <Link href={deepLinkFor(n)}>
      <div
        role="button"
        tabIndex={0}
        onClick={onRead}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onRead();
        }}
        className={cn(
          'group relative flex cursor-pointer flex-col gap-1 rounded-lg border bg-card p-3 shadow-sm transition-shadow hover:shadow-md',
          unread && 'border-l-[3px]',
        )}
        style={unread ? { borderLeftColor: color } : undefined}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {n.projectTitle}
              </span>
              <span
                className="rounded-full px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide"
                style={{ backgroundColor: kindMeta.bg, color: kindMeta.color }}
                title={kindMeta.title}
              >
                {kindMeta.label}
              </span>
            </div>
            <div className="mt-0.5 line-clamp-2 text-sm font-semibold leading-snug">
              {n.taskTitle}
            </div>
          </div>
          <ExternalLink className="mt-0.5 size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
          {formatRange(n) ? <span>{formatRange(n)}</span> : null}
          {n.assigneeRaw ? <span className="truncate">Назначено как: «{n.assigneeRaw}»</span> : null}
          {unread ? (
            <span
              className="ml-auto inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
              style={{ color }}
            >
              Новое
            </span>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

export default function MyTasksPage() {
  const { user } = useAuth();
  const { notifications, unreadCount, markRead, markAllRead, refresh, loading } = useNotifications();

  const grouped = useMemo(() => {
    const byStatus: Record<TaskNotificationStatus, TaskNotification[]> = {
      behind: [],
      active: [],
      planned: [],
      backlog: [],
      done: [],
    };
    for (const n of notifications) {
      // «Выполнено» показываем отдельной секцией вне зависимости от is_resolved,
      // остальные статусы — только активные (не resolved = задача ещё назначена и не завершена).
      if (n.status === 'done') {
        byStatus.done.push(n);
        continue;
      }
      if (n.isResolved) continue;
      byStatus[n.status].push(n);
    }
    for (const list of Object.values(byStatus)) {
      list.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
    return byStatus;
  }, [notifications]);

  const total = notifications.filter((n) => !n.isResolved && n.status !== 'done').length;

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--background)' }}>
      <Sidebar />
      <main className="flex-1 p-6 md:p-8 transition-[margin-left] duration-200 ease-out [margin-left:var(--app-sidebar-width)]">
        <div className="mx-auto max-w-5xl">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="mb-1 inline-flex items-center gap-2 rounded-full bg-muted/60 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <Bell className="size-3" /> Личный кабинет
              </div>
              <h1 className="text-2xl font-bold leading-tight">Мои задачи</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Задачи с планов-графиков проектов, на которые вы назначены исполнителем
                {user?.login ? (
                  <>
                    {' '}(сопоставление по{' '}
                    <code className="rounded bg-muted px-1 py-px text-[11px]">{user.login}</code>
                    ).
                  </>
                ) : '.'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refresh()}
                disabled={loading}
                className="gap-1.5"
              >
                <RefreshCcw className={cn('size-3.5', loading && 'animate-spin')} />
                Обновить
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => markAllRead()}
                disabled={unreadCount === 0}
                className="gap-1.5"
              >
                <CheckCheck className="size-3.5" />
                Прочитать всё
                {unreadCount > 0 ? (
                  <span className="ml-1 rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                    {unreadCount}
                  </span>
                ) : null}
              </Button>
            </div>
          </div>

          {notifications.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed bg-card p-12 text-center">
              <Bell className="size-8 text-muted-foreground/60" />
              <div className="text-sm font-medium">Пока никого не назначили на вас</div>
              <p className="max-w-md text-[12px] text-muted-foreground">
                Как только в любом плане-графике проекта появится задача, где в поле «Исполнители»
                указан ваш логин или ФИО, она появится здесь и вы получите уведомление.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {SECTIONS.map((section) => {
                const list = grouped[section.id];
                if (list.length === 0) return null;
                const Icon = section.Icon;
                return (
                  <section
                    key={section.id}
                    className="rounded-lg border bg-card/60 p-4"
                    style={{ background: section.bg }}
                  >
                    <header className="mb-3 flex items-center gap-2">
                      <span
                        className="inline-flex size-7 items-center justify-center rounded-full"
                        style={{ backgroundColor: `${section.color}20`, color: section.color }}
                      >
                        <Icon className="size-3.5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <h2 className="text-sm font-semibold" style={{ color: section.color }}>
                          {section.label} · {list.length}
                        </h2>
                        <p className="text-[11px] text-muted-foreground">{section.hint}</p>
                      </div>
                    </header>
                    <div className="grid gap-2 md:grid-cols-2">
                      {list.map((n) => (
                        <NotificationCard
                          key={n.id}
                          n={n}
                          color={section.color}
                          onRead={() => {
                            if (!n.isRead && !n.isResolved) markRead(n.id);
                          }}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}

              {total === 0 && grouped.done.length === 0 ? (
                <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed bg-card p-8 text-center text-sm text-muted-foreground">
                  <CheckCircle2 className="size-6 text-emerald-500" />
                  Все задачи разобраны.
                </div>
              ) : null}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
